/* eslint-disable max-len */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */

import React, { useRef, useState } from 'react';
import {
  ModelTreeNode,
  ModelTreeNodeDataFolder,
  ModelTreeNodeDataOption,
  ParsedTreeNodeType,
} from '../../modelConfiguration/data/modelOptionsTree';
import { TreeSelections } from '../../modelConfiguration/data/treeSelection';
import { TreeManipulation } from '../../modelConfiguration/logic/treeManipulation';
import { CatalogTagsTranslation } from '../../modelViewer/Data/catalogTagsTranslation';
import { ModelConfigurationTooltip } from './modelConfigurationTooltip';
import { SLTXUtil } from '../../fileFormats/sltx/sltxUtil';

interface ModelConfigurationLinearNodeProps {
  node: ModelTreeNode,
  pathParent: string,
  pathNode: string,
  treeSelectionState: TreeSelections,
  updateTreeSelectionState: (selection: TreeSelections) => void,
  updateOptionSelection: (groupID: number, optionID: number) => void,
  getResourcePath: (path: string) => string,
  languageSelected: string,
}

export function ModelConfigurationLinearNode(
  {
    node,
    pathParent,
    pathNode,
    treeSelectionState,
    updateTreeSelectionState,
    updateOptionSelection,
    getResourcePath,
    languageSelected,
  }: ModelConfigurationLinearNodeProps,
): JSX.Element {

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const isSelected = treeSelectionState.find(
    (sel) => sel.path === pathParent && sel.value === pathNode,
  ) !== undefined;

  const handleTreeNodeSelected = () => {

    // Remove the previous selection of the parent
    const newTreeSelectionState = treeSelectionState.filter(
      (sel) => sel.path.startsWith(pathParent) !== true,
    );

    // Add the new selection to the array
    newTreeSelectionState.push({
      path: pathParent,
      value: pathNode,
      type: node.type,
    });

    // Update the selection
    updateTreeSelectionState(newTreeSelectionState);
  };

  const handleOptionSelection = () => {

    updateOptionSelection(
      TreeManipulation.getGroupIDFromOptionNode(node),
      node.id,
    );
  };

  const renderNodeAsGroup = (): JSX.Element => (
    <div
      className="group"
      style={{ textAlignLast: 'left' }}
    >
      {CatalogTagsTranslation.getTranslation(node.tagTranslation, languageSelected) || node.name}
    </div>
  );

  const handleMouseEnter = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {

    if (containerRef.current) {
      const rect = event.currentTarget.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      const left = containerRect.x;
      const top = rect.bottom + 10;

      setTooltipPosition({ left, top });
    }
    setShowTooltip(true);

  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const renderNodeAsOption = (): JSX.Element => {
    let imagePath = '';

    if (node.name === SLTXUtil.DummyOption) {
      return (
        <div
          data-testid={`${node.name}`}
          data-testpath={`${node.path}`}
        />
      );
    }

    if (node.type === ParsedTreeNodeType.Option) {
      const nodeDataOption = node.data as ModelTreeNodeDataOption;
      if (nodeDataOption !== undefined) {

        // imagePath = sltxFile.getCompletePath(nodeData.thumbnailRef);
        imagePath = getResourcePath(nodeDataOption.thumbnailRef);
      }
    } else if (node.type === ParsedTreeNodeType.DomainGroup) {
      const nodeDataDomain = node.data as ModelTreeNodeDataFolder;
      if (nodeDataDomain !== undefined) {

        // imagePath = sltxFile.getCompletePath(nodeData.thumbnailRef)
        imagePath = `./catalog/${nodeDataDomain.thumbnailRef}`;
      }
    }

    return (
      <div>
        <div className="option-container">
          <div
            ref={containerRef}
            data-testid={`${node.name}`}
            data-testpath={`${node.path}`}
            className={isSelected ? 'option-selected' : 'option-unselected'}
            onClick={handleOptionSelection}
          >
            <div className="option-image-parent">
              <img
                className="option-image"
                src={imagePath}
                alt="option preview"
                title={CatalogTagsTranslation.getTranslation(node.tagTranslation, languageSelected) || node.name}
              />

            </div>
          </div>
          {/* Tag below the circle */}
          <div
            className="option-tag-string"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {CatalogTagsTranslation.getTranslation(node.tagTranslation, languageSelected) || node.name}
          </div>
        </div>
        {showTooltip && (
        <ModelConfigurationTooltip
          imageUrl={imagePath}
          tagString={CatalogTagsTranslation.getTranslation(node.tagTranslation, languageSelected) || node.name}
          tooltipPosition={tooltipPosition}
        />
        )}
      </div>

    );
  };

  const renderNodeAsDomainGroup = (): JSX.Element => {
    let imagePath = '';
    // State for managing tooltip visibility and content

    if (node.type === ParsedTreeNodeType.DomainGroup) {
      const nodeDataDomain = node.data as ModelTreeNodeDataFolder;
      if (nodeDataDomain !== undefined) {

        imagePath = getResourcePath(`catalog:${nodeDataDomain.thumbnailRef}`);
      }
    }

    const tagString = CatalogTagsTranslation.getTranslation(node.tagTranslation, languageSelected) || node.name;

    return (
      <div
        ref={containerRef}
        className={isSelected ? 'domain-selected' : 'domain-unselected'}
        onClick={handleTreeNodeSelected}
      >
        <div className="domain-content">
          <div className="domain-image-parent">
            <img className="domain-image" src={imagePath} alt="option preview" title={tagString} />

            {showTooltip && (
              <ModelConfigurationTooltip
                imageUrl={imagePath}
                tagString={tagString}
                tooltipPosition={tooltipPosition}
              />
            )}
          </div>
          <div
            className="domain-tag-string"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {tagString}
          </div>
        </div>
      </div>
    );
  };

  const renderNodeAsFolder = (): JSX.Element => {
    let imagePath = '';
    const nodeData = node.data as ModelTreeNodeDataFolder;
    if (nodeData !== undefined) {
      imagePath = getResourcePath(`${nodeData.thumbnailRef}`);
    }

    const tagString = CatalogTagsTranslation.getTranslation(node.tagTranslation, languageSelected) || node.name;

    return (
      <div
        ref={containerRef}
        className={isSelected ? 'domain-selected' : 'domain-unselected'}
        onClick={handleTreeNodeSelected}
      >
        <div className="domain-content">
          <div className="domain-image-parent">
            <img className="domain-image" src={imagePath} alt="option preview" title={tagString} />

            {showTooltip && (
              <ModelConfigurationTooltip
                imageUrl={imagePath}
                tagString={tagString}
                tooltipPosition={tooltipPosition}
              />
            )}
          </div>
          <div
            className="domain-tag-string"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {tagString}
          </div>
        </div>
      </div>
    );
  };

  const renderNodeAsDomain = (): JSX.Element => (
    <div
      className="domain"
    >
      {CatalogTagsTranslation.getTranslation(node.tagTranslation, languageSelected) || node.name}
    </div>
  );

  const renderNode = (): JSX.Element => {

    if (node.type === ParsedTreeNodeType.Root) {
      // Root does not have visual representation
      return <div />;
    }

    if (!node.singleOptionParent && node.type === ParsedTreeNodeType.Group) {
      return renderNodeAsGroup();
    }

    if (node.type === ParsedTreeNodeType.Option) {
      return renderNodeAsOption();
    }

    if (node.type === ParsedTreeNodeType.Folder) {
      return renderNodeAsFolder();
      // return renderNodeAsGroup();
    }

    if (!node.singleOptionParent && node.type === ParsedTreeNodeType.Domain) {
      return renderNodeAsDomain();
    }

    if (node.type === ParsedTreeNodeType.DomainGroup) {
      return renderNodeAsDomainGroup();
    }

    return (
      <div />
    );
  };

  return (
    <>
      {renderNode()}
    </>
  );

}
